import type { SdkWidgetSettings } from '../types';
import { el, on } from './dom-utils';
import { createVeridionBranding } from './branding';

export interface BannerCallbacks {
  onAcceptAll: () => Promise<void>;
  onRejectAll: () => Promise<void>;
  onManage: () => void;
}

const DEFAULT_TITLE = 'We value your privacy';
const DEFAULT_DESC =
  'We use cookies and similar technologies to provide the best experience on our website. You can choose your preferences below.';

/**
 * Renders the consent banner bar and returns the root element.
 * Supports variants: default (full bar), compact (denser bar), minimal (floating pill).
 */
export function createBanner(
  settings: SdkWidgetSettings,
  callbacks: BannerCallbacks,
): HTMLElement {
  const variant = (settings.bannerVariant || 'default') as 'default' | 'compact' | 'minimal';
  const title = settings.bannerTitle || DEFAULT_TITLE;
  const desc = settings.bannerDescription || DEFAULT_DESC;

  // ── Body (left side) ──
  const bodyEl = el('div', { className: 'vrd-banner__body' });

  if (settings.logoUrl && variant !== 'minimal') {
    bodyEl.appendChild(
      el('img', { className: 'vrd-logo', src: settings.logoUrl, alt: settings.companyName || 'Logo' }),
    );
  }

  bodyEl.appendChild(el('h2', { className: 'vrd-banner__title', textContent: title }));
  if (variant !== 'minimal') {
    bodyEl.appendChild(el('p', { className: 'vrd-banner__desc', textContent: desc }));
  }

  // ── Action buttons (right side) ──
  const acceptBtn = el('button', { className: 'vrd-btn vrd-btn--primary' }) as HTMLButtonElement;
  acceptBtn.appendChild(document.createTextNode('Accept All'));
  acceptBtn.appendChild(el('span', { className: 'vrd-btn__spinner', 'aria-hidden': 'true' }));

  const rejectBtn = el('button', { className: 'vrd-btn vrd-btn--secondary' }) as HTMLButtonElement;
  rejectBtn.appendChild(document.createTextNode('Reject All'));
  rejectBtn.appendChild(el('span', { className: 'vrd-btn__spinner', 'aria-hidden': 'true' }));

  const manageBtn = el('button', { className: 'vrd-btn vrd-btn--link', textContent: 'Manage Preferences' }) as HTMLButtonElement;

  const allBtns = [acceptBtn, rejectBtn, manageBtn];

  function setBannerLoading(active: boolean): void {
    for (const btn of allBtns) btn.disabled = active;
  }

  on(acceptBtn, 'click', async () => {
    setBannerLoading(true);
    acceptBtn.classList.add('vrd-btn--loading');
    try { await callbacks.onAcceptAll(); } finally {
      setBannerLoading(false);
      acceptBtn.classList.remove('vrd-btn--loading');
    }
  });
  on(rejectBtn, 'click', async () => {
    setBannerLoading(true);
    rejectBtn.classList.add('vrd-btn--loading');
    try { await callbacks.onRejectAll(); } finally {
      setBannerLoading(false);
      rejectBtn.classList.remove('vrd-btn--loading');
    }
  });
  on(manageBtn, 'click', callbacks.onManage);

  const actions = el('div', { className: 'vrd-banner__actions' }, rejectBtn, manageBtn, acceptBtn);

  const main = el('div', { className: 'vrd-banner__main' }, bodyEl, actions);
  const header = el('div', { className: 'vrd-banner__header' });
  header.appendChild(createVeridionBranding(settings.theme));

  const layout = (settings.bannerLayout || 'bar') as 'bar' | 'dialog';
  const isDialog = layout === 'dialog';

  const banner = el('div', {
    className: `vrd-banner vrd-banner--${variant}${isDialog ? ' vrd-banner--dialog' : ''}`,
  }, header, main);
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-label', 'Cookie consent');

  if (isDialog) {
    const overlay = el('div', { className: 'vrd-overlay vrd-banner-overlay' });
    const box = el('div', { className: 'vrd-banner-dialog-box' });
    box.appendChild(banner);
    overlay.appendChild(box);
    return overlay;
  }

  return banner;
}
